import { StackZIndexContext } from "@tamagui/z-index-stack";
import { isAndroid, isIos, isTouchable, isWeb, useIsomorphicLayoutEffect } from "@tamagui/constants";
import { createStyledContext, useMedia } from "@tamagui/core";
import { withStaticProperties } from "@tamagui/helpers";
import { PortalHost, PortalItem } from "@tamagui/portal";
import React, { createContext, useContext, useEffect, useId } from "react";
import { jsx } from "react/jsx-runtime";
const CurrentAdaptContextScope = createContext(""),
  AdaptContext = createStyledContext({
    Contents: null,
    scopeName: "",
    portalName: "",
    platform: null,
    setPlatform: x => {},
    when: null,
    setChildren: null,
    setWhen: () => {}
  }),
  ProvideAdaptContext = ({
    children,
    ...context
  }) => {
    const scope = context.scopeName || "";
    return /* @__PURE__ */jsx(CurrentAdaptContextScope.Provider, {
      value: scope,
      children: /* @__PURE__ */jsx(AdaptContext.Provider, {
        scope,
        ...context,
        children
      })
    });
  },
  useAdaptContext = (scope = "") => {
    const contextScope = useContext(CurrentAdaptContextScope);
    return AdaptContext.useStyledContext(scope === "" && contextScope || scope);
  },
  AdaptPortals = /* @__PURE__ */new Map(),
  AdaptParent = ({
    children,
    Contents,
    scope,
    portal
  }) => {
    const portalName = `AdaptPortal${scope}`,
      id = useId();
    let FinalContents = Contents || AdaptPortals.get(id);
    FinalContents || (FinalContents = () => /* @__PURE__ */jsx(PortalHost, {
      name: portalName,
      forwardProps: typeof portal == "boolean" ? void 0 : portal?.forwardProps
    }), AdaptPortals.set(id, FinalContents)), useEffect(() => () => {
      AdaptPortals.delete(id);
    }, []);
    const [when, setWhen] = React.useState(null),
      [platform, setPlatform] = React.useState(null),
      [children2, setChildren] = React.useState(null);
    return /* @__PURE__ */jsx(ProvideAdaptContext, {
      Contents: FinalContents,
      when,
      platform,
      setPlatform,
      setWhen,
      setChildren,
      portalName,
      scopeName: scope,
      children
    });
  },
  AdaptContents = ({
    scope,
    ...rest
  }) => {
    const context = useAdaptContext(scope);
    if (!context?.Contents) throw new Error(process.env.NODE_ENV === "production" ? "tamagui.dev/docs/intro/errors#warning-002" : "You're rendering a Tamagui <Adapt /> component without nesting it inside a parent that is able to adapt.");
    return React.createElement(context.Contents, {
      ...rest,
      key: "stable"
    });
  };
AdaptContents.shouldForwardSpace = !0;
const Adapt = withStaticProperties(function (props) {
    const {
        platform,
        when,
        children,
        scope
      } = props,
      context = useAdaptContext(scope),
      scopeName = scope ?? context.scopeName,
      enabled = useAdaptIsActiveGiven(props);
    useIsomorphicLayoutEffect(() => {
      context?.setWhen?.(when || enabled), context?.setPlatform?.(platform || null);
    }, [when, platform, context, enabled]), useIsomorphicLayoutEffect(() => () => {
      context?.setWhen?.(null), context?.setPlatform?.(null);
    }, []);
    let output;
    if (typeof children == "function") {
      const Component = context?.Contents;
      output = children(Component ? /* @__PURE__ */jsx(Component, {}) : null);
    } else output = children;
    return useIsomorphicLayoutEffect(() => {
      typeof children == "function" && output !== void 0 && context?.setChildren(output);
    }, [output]), /* @__PURE__ */jsx(StackZIndexContext, {
      children: /* @__PURE__ */jsx(CurrentAdaptContextScope.Provider, {
        value: scopeName,
        children: enabled ? output : null
      })
    });
  }, {
    Contents: AdaptContents
  }),
  AdaptPortalContents = props => {
    const {
      portalName
    } = useAdaptContext(props.scope);
    return /* @__PURE__ */jsx(PortalItem, {
      hostName: portalName,
      children: props.children
    });
  },
  useAdaptIsActiveGiven = ({
    when,
    platform
  }) => {
    const media = useMedia();
    if (when == null && platform == null) return !1;
    let enabled = !1;
    return platform === "touch" ? enabled = isTouchable : platform === "native" ? enabled = !isWeb : platform === "web" ? enabled = isWeb : platform === "ios" ? enabled = isIos : platform === "android" && (enabled = isAndroid), platform && enabled == !1 ? !1 : (when && typeof when == "string" && (enabled = media[when]), enabled);
  },
  useAdaptIsActive = scope => {
    const props = useAdaptContext(scope);
    return useAdaptIsActiveGiven(props);
  };
export { Adapt, AdaptContents, AdaptContext, AdaptParent, AdaptPortalContents, ProvideAdaptContext, useAdaptContext, useAdaptIsActive };
//# sourceMappingURL=Adapt.mjs.map
